/** @module FormulatedProduct/FoodSupplierItemForm */
define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/query",
	"dojo/store/Memory",
	"dijit/registry",
	"csi",
	"csi/_base/underscore",
	"csi/comm",
	"csi/def",
	"csi/def/Action",
	"csi/def/Field",
	"csi/def/Relationship",
	"csi/def/ViewRef",
	"csi/dgrid/MatrixEditorWithCheckBox",
	"csi/form/Widget",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/state",
	"csi/store/_base",
	"csi/store/Names",
	"csi/xml",
	"csi/xml/Query",
	"FormulatedProduct/model/foodSource",
	"Sourcing"
], function(array, declare, lang, domClass, construct, query, Memory, registry, csi, _, comm, def, Action, Field, Relationship, ViewRef, MatrixEditorWithCheckBox, Widget, html, locale, reflection, state, csiStore, Names, xml, Query, {ATTR_SOURCE_SUPPLIER_ITEMS, ATTR_SOURCING_SOURCES, TYPE_FOOD_SOURCE}, Sourcing) {


	return declare(Widget, {
		'class': 'csi-form-view-block',

		LINE_ITEM: "QuoteSRLineItem",
		QUOTE_ROUTING: "QuoteRouting",
		SUPPLIER: "Supplier",
		FACTORY: "Factory",
		SUPPLIER_FACTORY: "SupplierFactory",
		TEMPLATE: "FoodSupplierItemTemplate",

		// String: URL of SupplierRequest if invoked from there
		urlRequest: null,

		// Node: Product
		nodeProduct: null,

		// String[]:selected products
		_productUrls: null,

		/** @property {boolean}: true if creating SupplierItem under SupplierRequest - populated on NewSupplierItemMixin onCreatePopupParams */
		bSupplierRequest: false,

		/** @property {boolean}: true if creating SupplierItem under Supplier Quote Template */
		bTemplate: false,

		/** @property {boolean} isFDDEnabled -  indicate if FDD is enabled for this product - populated on NewSupplierItemMixin onCreatePopupParams  */
		isFDDEnabled: false,

		setProduct: function(/*Node*/product) {
			this.nodeProduct = product;
			this.cacheView.cacheNode(product);

			if (product) {
				const subtype = product.$Type == "Food" ? "FoodSubtype" : "FormulatedMaterialSubtype";
				this.addFixedValue(locale.type(subtype), product.Subtype);
			}

			// update form node with new parent product
			const supplierItem = this.getFormNode();
			const productSource = this.cacheView.get(supplierItem.__Parent__);
			this.onCreateFormNode(productSource, TYPE_FOOD_SOURCE);

			// refresh aggregate stores since product is now different
			this.resetAggregateStores((field) => field.getControlName() !== this.LINE_ITEM);
		},


		_getRootRequest: function(/*Node*/node) {
			// summary: get root request for nodes needed by view
			let urls = [];
			let paths = [];

			if (node) {
				urls.push(node.$URL);
				paths.push("Child:__Parent__/Child:__Parent__");
			}
			let srNode = this.cacheView.getNode(this.urlRequest);
			if (srNode) {
				urls.push(srNode.$URL);
				paths.push("Child:LineItems/Child:SRLineItemProduct");
			}

			if (this.nodeProduct) {
				urls.push(this.nodeProduct.$URL);
				paths.push("Child:ProductSourcing");
			}

			if (urls.length) {
				return comm.getNodeRequest(urls, null, true).addDependencyPaths(paths);
			}

			// if node is null, then no request will be made from this.inherited
			return null;
		},

		/**
		 * @override
		 * Attach ProductSource to product sourcing node in context
		 */
		onCreateFormNode(node, type) {
			if (type === "FoodSource") {
				const {nodeProduct} = this;
				if (nodeProduct) {
					node.__Parent__ = nodeProduct.ProductSourcing;
				}
			}
		},

		/**
		 * @override
		 * Hide template field if no values
		 */
		onSetDefaultValue(field, store, value) {
			this.inherited(arguments);

			if (field.getControlName() == this.TEMPLATE) {
				if (store.isStoreEmpty()) {
					this.setFieldHidden(field, true);
				}
			}
		},

		/**
		 * @override
		 * Attach form node to parent product
		 */
		onWriteFormReport(field, writer, formInfo) {
			const {nodeUrl, parentUrl} = formInfo;

			// if creating SupplierItem directly under ProductSource, then nothing else to do
			if (parentUrl) {
				return;
			}

			// if creating under product, then create path up to product
			const {nodeProduct} = this;
			if (nodeProduct) {
				writer.createNode(null, "FoodSource")
					.attach(nodeProduct.ProductSourcing, ATTR_SOURCING_SOURCES)
					.changeAttribute(ATTR_SOURCE_SUPPLIER_ITEMS, xml.ATTR_REFSET, [nodeUrl]);
			}
		},

		_buildForm: function() {
			this.inherited(arguments);
			this._updateStatistics();
		},

		/**
		 * Handle retrieving of store and its action
		 * @param {String} gotoDialog - id of gotoDialog
		 * @param {String} params - different field params
		 * @param {Object} rel - field relationship
		 */
		_getStore: function(gotoDialog, params, rel) {
			let storeOptions = csiStore.namesOptionsByRelationship(this.getContext(), rel);
			storeOptions.action = lang.mixin(new Action(), {
				m_name: locale.resource("AggregateInline_Name", [locale.typePlural(gotoDialog)]),
				m_viewRef: lang.mixin(new ViewRef(), params),
				m_fixedQuery: params.m_query // possible extra filter
			});

			return new Names(storeOptions);
		},

		_resolveView: function() {
			// summary: add supplier and template fields when necessary
			let fd = this.defView.m_formData;
			if (!fd) {
				this.inherited(arguments);
				return;
			}

			let sourceNode = this.getViewNode();
			let parentNode = fd.getSource().getParent();
			
			let bProductSource = parentNode?.$Type == "FoodSource";
			this.bTemplate = parentNode.$Type === "SiteLibSupplierItem"
			let f;

			const {bSupplierRequest, bTemplate, isFDDEnabled} = this;
			const sourceFieldId = isFDDEnabled ? this.FACTORY : this.SUPPLIER;
			let selectType = def.FIELD_FILTERING_SELECT;

			if (!sourceNode) {  // sourceNode means copy
				selectType = def.FIELD_MULTI_SELECT;
			}

			// insert template field if creating from scratch - not wanted when copying from existing quote
			if (!sourceNode && !bTemplate) {
				this.insertField(lang.mixin(new Field(), {
					idParameter: "TemplateURL",
					m_bInitialize: true,
					m_label: this.TEMPLATE,
					m_onChange: "_onChangeTemplate",
					m_typeControl: def.FIELD_FILTERING_SELECT,
					m_relationship: lang.mixin(new Relationship(), {
						m_url: "centric://REFLECTION/INSTANCE/SiteLibSupplierItem/SiteLibSupplierItem",
						m_id: "FoodSupplierItemTemplates"
					})
				}), 0);
			}

			// init line item field if form invoked from SupplierRequest or a Copy
			if (bSupplierRequest || this.isCopy()) {
				this.urlRequest = parentNode.$URL;

				if (!bTemplate) {
					if (this.isCopy() && state.get("userType") != "Supplier") {
						// only put this field on the form if you can see the Product
						const product = this.cacheView.getPathNode(sourceNode, "Child:__Parent__/Child:__Parent__/Child:__Parent__");
						if (product != null) {
							const subtype =
								(parentNode.RequestType == "DataPackageContainerType:FormulatedMaterial" || parentNode.$Type == "FormulatedMaterialSourcing")
									? "FormulatedMaterialSubtype" : "FoodSubtype";
							this.insertField(lang.mixin(new Field(), {
								m_label: locale.type(subtype),
								m_typeControl: def.FIELD_FILTERING_SELECT,
								m_relationship: lang.mixin(new Relationship(), {
									m_typeNode: subtype
								})
							}), 0);
						}
					}
					const q = new Query().RefAttr("SRLineItemProduct", xml.OP_NOT_EQUALS, csi.EMPTY_URL);
					this.insertField(lang.mixin(new Field(), {
						bForceEditable: !sourceNode,
						m_bRequired: true,
						m_id: this.LINE_ITEM,
						m_onChange: "_onChangeLineItem",
						m_typeControl: selectType,
						widgetParams: {defaultAll: true},
						m_relationship: lang.mixin(new Relationship(), {
							m_url: this.urlRequest,
							m_id: bSupplierRequest ? "LineItems" : "ProductSources/Child:SRLineItems",
							m_query: q
						}),
						m_right: sourceNode ? xml.RIGHT_NONE : null
					}), 1);
				}

				this._productUrls = parentNode.LineItems;
			}

			// insert supplier field if creating from outside of ProductSource
			if (!bProductSource && !bTemplate) {
				f = this.insertField(lang.mixin(new Field(), {
					bForceEditable: !sourceNode,
					m_bRequired: true,
					idParameter: "SrcURL",
					m_id: "ProductSources",
					m_label: sourceFieldId,
					m_path: xml.path(xml.ATTR_PARENT, xml.ATTR_PARENT),
					// prevent the form from sending a request of changing productSources to the function queryAggregateStore
					// Setting omitFromReport to false will return null from queryAggregateStore, which breaks the form
					// creating the ProductSource explicitly in onWriteFormReport (to get up to ProductSourcing) and don’t want this to conflict with that.
					omitFromReport: true,
				}), 0);

				const designatedProductSource = this.getCache().getNode(parentNode.DesignatedProductSource);
				this.defView.m_formData.setFieldInitialValue(this.SUPPLIER, designatedProductSource ? [designatedProductSource.Supplier] : []);
				this.defView.m_formData.setFieldInitialValue(this.FACTORY, designatedProductSource ? [designatedProductSource.SupplierFactory] : []);

				if(this.isCopy()){
					const cache = this.getCache();
					const fieldName = sourceFieldId;
					const productSource = cache.getNode(sourceNode.__Parent__);
					const node = isFDDEnabled ? cache.getNode(productSource.SupplierFactory) : cache.getNode(productSource.Supplier);
					this.addFixedValue(fieldName, node.$URL);
					this.defView.m_formData.setFieldInitialValue(xml.ATTR_NAME, "");
	
			}

				if (this.urlRequest) {
					// change query if on SupplierRequest and allow for All - add SupplierFactory if FDD is enabled.
					// Not using the new aggregateStore for this special case
					if(isFDDEnabled){
						f.m_relationship = lang.mixin(new Relationship(), {
							m_url: this.urlRequest,
							m_id: "SRSources/Child:SupplierFactory"
						});
					} else {
						f.m_relationship = lang.mixin(new Relationship(), {
							m_url: this.urlRequest,
							m_id: this.isCopy() ? "ProductSources/Child:Supplier": "SRSources/Child:Supplier",
							m_query: new Query().Attr("AutoCreateProductSourceOnSR", xml.OP_EQUALS, true).SortByNode(xml.PROP_NAME) // only Suppliers with AutoCreateProductSourceOnSR is set to true
						});
					}
					
				}
			}

			this.inherited(arguments);
		},

		onChangeField(field) {
			this.inherited(arguments);
			this._updateStatistics();
		},



		_updateStatistics: function() {
			const {cacheView, defView, rightWidget, _matrix} = this;

			if (!rightWidget)
				return; // nothing to do

			let products = [];
			let suppliersLength = 1;

			const parentNode = defView.getSource().getParent();
			if (parentNode.$Type == "FoodSourcing") {
				products = [parentNode.__Parent__];
			} else if (parentNode.$Type == "FoodSource") {
				products = [cacheView.getNode(parentNode.__Parent__).__Parent__];
			} else {
				const lineItems = this.getFieldValue(this.LINE_ITEM);
				if (Array.isArray(lineItems)) {
					for (const li of lineItems) {
						products.push(cacheView.getNode(li).SRLineItemProduct);
					}
				}
			}

			if (this.getField(this.SUPPLIER)) {
				suppliersLength = this.getFieldValue(this.SUPPLIER).length;
			}
		},

		_onChangeTemplate: function(/*def.View*/defView, /*Field*/field, /*Any*/value) {
			// summary: initialize name based on selection if color name not already set
			let f = this.defView.getField(xml.ATTR_NAME);
			if (f && f.isDefaultValue()) {
				const nodeName = (value != csi.EMPTY_URL) ? this.cacheView.getNode(value, true).$Name : "";
				this.setFieldValue(f, nodeName, true);
			}
		},

		/**
		 * update dimensions based on selected product if line item field is visible
		 * @param {View} defView 
		 * @param {Field} field 
		 * @param {Any} value 
		 * @returns 
		 */
		_onChangeLineItem: function(defView, field, value) {
			if (!this.isFieldVisible(field)) {
				return;
			}

			// set selected products
			if (Array.isArray(value) && value.length > 1) {
				this._productUrls = value;
			}

			// setup new product
			const lineItem = this.cacheView.getNode(value);
			const urlProduct = lineItem?.SRLineItemProduct;
			this.setProduct(this.cacheView.getNode(urlProduct));
		},
	});
});
